import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import React from 'react';
import { match } from 'ts-pattern';

import { QUERY_ROOT_ID, useCompleteFragment } from '@eluve/apollo-client';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Icon,
  Stack,
  Text,
} from '@eluve/components';
import { FragmentOf } from '@eluve/graphql.tada';

import {
  modelArgsFragment,
  modelPromptTemplatesFragment,
} from './eval.operations';

type ModelPromptTemplate = FragmentOf<
  typeof modelPromptTemplatesFragment
>['llmModelPromptTemplates'][number];

export interface ModelPromptTemplateSearchProps {
  selectedIds: string[];
  onSelectionChanged: (selectedIds: string[]) => void;
  filter?: (m: ModelPromptTemplate) => boolean;
  isSingleSelection?: boolean;
}

export const ModelPromptTemplateSearch: React.FC<
  ModelPromptTemplateSearchProps
> = ({
  onSelectionChanged,
  selectedIds,
  isSingleSelection = false,
  filter = () => true,
}) => {
  const { llmModelPromptTemplates } = useCompleteFragment({
    fragment: modelPromptTemplatesFragment,
    key: QUERY_ROOT_ID,
  });

  const { llmModelArgs } = useCompleteFragment({
    fragment: modelArgsFragment,
    key: QUERY_ROOT_ID,
  });

  const modelPromptTemplates = (llmModelPromptTemplates ?? []).filter(filter);
  const groupedByModel = groupBy(
    modelPromptTemplates,
    (template) => template.modelArgsId,
  );

  const modelArgsLookup = keyBy(llmModelArgs, (m) => m.id);

  const getNewSelection = (id: string) => {
    return match(isSingleSelection)
      .with(true, () => {
        return match(selectedIds)
          .with([id], () => [])
          .otherwise(() => [id]);
      })
      .otherwise(() => {
        return selectedIds.includes(id)
          ? selectedIds.filter((selectedId) => selectedId !== id)
          : [...selectedIds, id];
      });
  };

  return (
    <Command className="border">
      <CommandInput placeholder="Search for a model prompt template" />
      <CommandEmpty>No Model Prompt Templates found</CommandEmpty>
      <CommandList>
        {Object.entries(groupedByModel).map(([modelId, templates]) => {
          // Safe because we're mapping over the entries of the object and accessing
          // based on the object key
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const modelArgs = modelArgsLookup[modelId]!;
          return (
            <CommandGroup
              heading={
                <div>
                  <Text>{modelArgs.name}</Text>
                  <pre>{JSON.stringify(modelArgs.args)}</pre>
                </div>
              }
              key={modelId}
            >
              {templates.map((modelTemplate) => (
                <CommandItem
                  key={modelTemplate.id}
                  value={`${modelTemplate.template.name}-${modelArgs.name}-${modelArgs.modelType}-${modelTemplate.id}`}
                  onSelect={() => {
                    onSelectionChanged(getNewSelection(modelTemplate.id));
                  }}
                >
                  {/* {modelTemplate.template.name} */}
                  <Stack
                    className="group"
                    direction="horizontal"
                    align="center"
                    justify="between"
                    gap={1}
                  >
                    <Stack
                      direction="horizontal"
                      align="center"
                      gap={2}
                      fullWidth={false}
                    >
                      {selectedIds.includes(modelTemplate.id) ? (
                        <Icon
                          name="Check"
                          className="text-bg-lightgreen-strong"
                        />
                      ) : (
                        <div className="size-4" />
                      )}
                      <Text>{modelTemplate.template.name}</Text>
                    </Stack>
                    <pre className="text-text-tertiary">
                      {JSON.stringify(modelArgs.args)}
                    </pre>
                  </Stack>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </Command>
  );
};
